import { getTranslate } from '../../localization'
import {
  PaymentFollowUpReason,
  PaymentFollowUpAction,
  PaymentSettlementOutcome,
  PaymentFollowUpState
} from '../../types/paymentFollowUps'

// Literal getTranslate calls, one per value, so localization/coverage.test.ts can see every key.

export const FOLLOW_UP_REASONS: PaymentFollowUpReason[] = [
  'NeedsReversal',
  'ManualRefund',
  'SecondBnplProvider',
  'ProviderReversal',
  'SettlementReview'
]

export const FOLLOW_UP_STATES: PaymentFollowUpState[] = ['Open', 'Resolved']

export function reasonLabel(reason: PaymentFollowUpReason): string {
  switch (reason) {
    case 'NeedsReversal':
      return getTranslate('Needs reversal')
    case 'ManualRefund':
      return getTranslate('Manual refund')
    case 'SecondBnplProvider':
      return getTranslate('Second BNPL provider')
    case 'ProviderReversal':
      return getTranslate('Provider reversal')
    case 'SettlementReview':
      return getTranslate('Settlement review')
    default:
      return reason
  }
}

/** What operations should do about a follow-up of this reason. */
export function reasonAction(
  reason: PaymentFollowUpReason,
  gateway?: string | null,
  action?: PaymentFollowUpAction | null
): string {
  switch (action) {
    case 'ReviewWalletCredit':
      return getTranslate(
        'Verify the wallet credit or release with finance before issuing another credit or refund.'
      )
    case 'ReviewProviderReversal':
      return getTranslate(
        'Verify the provider approval and arrange a reversal if still outstanding.'
      )
    case 'ReviewProviderConflict':
      return getTranslate(
        'Review the approvals with both providers and confirm which liability remains.'
      )
    case 'ReviewRefund':
      return getTranslate(
        'Verify whether the gateway already refunded the payer. If settlement is still outstanding, arrange the appropriate refund.'
      )
  }
  switch (reason) {
    case 'NeedsReversal':
      return getTranslate('Have the provider reverse the approval.')
    case 'ManualRefund':
      return gateway === 'Tara'
        ? getTranslate(
            'Verify the approval with Tara and arrange a reversal if still outstanding. Do not issue a bank refund solely because this payment is marked CASH.'
          )
        : getTranslate(
            'Verify whether the gateway already refunded the payer. If settlement is still outstanding, arrange the appropriate refund.'
          )
    case 'SecondBnplProvider':
      return getTranslate('Settle with both providers.')
    case 'SettlementReview':
      return getTranslate(
        'Verify the payment, booking and previous settlements before deciding what remains to be settled.'
      )
    case 'ProviderReversal':
      return getTranslate(
        'Tell the provider the guarantee is smaller and settle the difference with them.'
      )
    default:
      return ''
  }
}

export function settlementOutcomeLabel(
  outcome: PaymentSettlementOutcome
): string {
  switch (outcome) {
    case 'GatewayRefund':
      return getTranslate('Confirmed gateway refund')
    case 'ProviderReversal':
      return getTranslate('Confirmed provider reversal')
    case 'AcceptedWalletSettlement':
      return getTranslate('Verified accepted wallet settlement')
    case 'WalletCreditConfirmed':
      return getTranslate('Verified wallet credit repair')
    case 'ReviewedNoFurtherAction':
      return getTranslate('Reviewed: no further action')
  }
}

export function settlementActionLabel(action: PaymentFollowUpAction): string {
  switch (action) {
    case 'ReviewRefund':
      return getTranslate('Payer repayment review')
    case 'ReviewProviderReversal':
      return getTranslate('Provider reversal review')
    case 'ReviewWalletCredit':
      return getTranslate('Wallet credit or release review')
    case 'ReviewProviderConflict':
      return getTranslate('Provider conflict review')
  }
}

export function stateLabel(state: PaymentFollowUpState): string {
  return state === 'Open' ? getTranslate('Open') : getTranslate('Resolved')
}

export function emptyStateText(state: PaymentFollowUpState): string {
  return state === 'Open'
    ? getTranslate('No open payment follow-ups.')
    : getTranslate('No resolved payment follow-ups.')
}

/**
 * adotel's own refusals are worth showing (in Persian when translated); anything else, such as
 * a network failure or an internal BFF message, gets a generic translated text.
 */
export function followUpErrorMessage(error: {
  message?: string
  graphQLErrors?: ReadonlyArray<{ message: string }>
}): string {
  const message = error.graphQLErrors?.[0]?.message ?? ''
  switch (message) {
    case 'This follow-up changed. Refresh before recording settlement.':
      return getTranslate(
        'This follow-up changed. Refresh before recording settlement.'
      )
    case 'This follow-up is already resolved.':
      return getTranslate('This follow-up is already resolved.')
    case 'Only platform administrators can view payment follow-ups.':
      return getTranslate(
        'Only platform administrators can view payment follow-ups.'
      )
    case 'Only platform administrators can resolve payment follow-ups.':
      return getTranslate(
        'Only platform administrators can resolve payment follow-ups.'
      )
    case 'Payment follow-up not found.':
      return getTranslate('Payment follow-up not found.')
    default:
      return getTranslate('Something went wrong. Please try again.')
  }
}
